// Function index:
//   renderUserUsage(host) — render per-user usage dashboard with period tabs.
//       Shows tokens and USD by model, with summary row.

import { getUserUsage } from '../../api.js';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'all', label: 'All Time' },
] as const;

function createSummaryRow(totalInputTokens: number, totalOutputTokens: number, totalUsd: number): HTMLTableRowElement {
  const totalRow = document.createElement('tr');
  totalRow.className = 'usage-summary';

  const totalLabel = document.createElement('td');
  totalLabel.textContent = `Total: ${(totalInputTokens + totalOutputTokens).toLocaleString()} tokens`;
  totalRow.appendChild(totalLabel);

  const emptyCell1 = document.createElement('td');
  totalRow.appendChild(emptyCell1);

  const emptyCell2 = document.createElement('td');
  totalRow.appendChild(emptyCell2);

  const totalUsdCell = document.createElement('td');
  totalUsdCell.textContent = `$${totalUsd.toFixed(2)}`;
  totalRow.appendChild(totalUsdCell);

  return totalRow;
}

export async function renderUserUsage(host: HTMLElement): Promise<void> {
  host.innerHTML = 'Loading…';
  let currentPeriod: string = 'today';

  const render = async (): Promise<void> => {
    try {
      const data = await getUserUsage(currentPeriod);
      host.innerHTML = '';

      // Tab buttons
      const tabsDiv = document.createElement('div');
      tabsDiv.className = 'usage-tabs';

      for (const period of PERIODS) {
        const btn = document.createElement('button');
        btn.textContent = period.label;
        btn.dataset.period = period.id;
        btn.className = period.id === currentPeriod ? 'active' : '';

        btn.addEventListener('click', async () => {
          currentPeriod = period.id;
          await render();
        });

        tabsDiv.appendChild(btn);
      }

      host.appendChild(tabsDiv);

      // Table
      const table = document.createElement('table');
      table.className = 'usage-table';

      // Header
      const thead = document.createElement('thead');
      const headerRow = document.createElement('tr');

      const headers = ['Model', 'Input Tokens', 'Output Tokens', 'Total USD'];
      for (const header of headers) {
        const th = document.createElement('th');
        th.textContent = header;
        headerRow.appendChild(th);
      }
      thead.appendChild(headerRow);
      table.appendChild(thead);

      // Body
      const tbody = document.createElement('tbody');
      const models = Object.keys(data.tokens_by_model).sort();

      // Accumulate totals while iterating
      let totalInputTokens = 0;
      let totalOutputTokens = 0;

      for (const model of models) {
        const tokens = data.tokens_by_model[model];
        const usd = data.usd_by_model[model] ?? 0;

        if (!tokens) continue;

        totalInputTokens += tokens.input_tokens;
        totalOutputTokens += tokens.output_tokens;

        const row = document.createElement('tr');

        const cells = [
          model,
          tokens.input_tokens.toLocaleString(),
          tokens.output_tokens.toLocaleString(),
          `$${usd.toFixed(2)}`,
        ];

        for (const cell of cells) {
          const td = document.createElement('td');
          td.textContent = cell;
          row.appendChild(td);
        }

        tbody.appendChild(row);
      }

      // Add summary row using helper
      tbody.appendChild(createSummaryRow(totalInputTokens, totalOutputTokens, data.total_usd));
      table.appendChild(tbody);

      host.appendChild(table);
    } catch (err) {
      host.innerHTML = '';
      console.error('usage-fetch error:', err);
      const error = document.createElement('div');
      error.textContent = 'Could not load usage data';
      error.className = 'usage-error';
      host.appendChild(error);
    }
  };

  await render();
}
